import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import membotImageSrc from 'images/membot.png';
import { useEventGeneratorClickCta } from 'src/analytics/useEventGeneratorClickCta';
import theme from 'src/utils/theme';
import { ContentCta } from 'src/features/Tags/types/contentCta';
import { useCtaHref, useImmersiveVideoCtaHref } from 'src/hooks/useCtaHref';
import { useExternalHref } from 'src/hooks/useExternalHref';
import { Button } from 'src/components';
import { useMediaQuerySSR } from 'src/hooks/useStateSSR';
import { useTargetMessages } from 'src/hooks/useTargetMessages';
import interestsIconsSrc from '../images/interests-icons.png';
import * as S from './styles';
import * as msgsData from './messages';

const { msgs, ...localesWithMessages } = msgsData;

function useCtaImage(contentCta: ContentCta): React.JSX.Element {
  const link = useCtaLink(contentCta);

  switch (contentCta.type) {
    case 'interesting-content':
      return (
        <S.InterestsImageWrapper>
          <S.Image src={interestsIconsSrc} alt="" />
        </S.InterestsImageWrapper>
      );
    case 'immerse':
      return (
        <S.ImmerseImageWrapper href={link.href} aria-label={link.text}>
          <S.Image src={contentCta.previewUrl} alt="" />

          <S.PlayIcon />
        </S.ImmerseImageWrapper>
      );
    case 'membot':
      return (
        <S.MembotImageWrapper>
          <S.Image src={membotImageSrc} alt="" />
        </S.MembotImageWrapper>
      );
  }
}

function useCtaText(contentCta: ContentCta): string {
  const intl = useIntl();
  const targetMsgs = useTargetMessages(localesWithMessages);

  switch (contentCta.type) {
    case 'interesting-content':
      return intl.formatMessage(targetMsgs.interestingContentText);
    case 'immerse':
    case 'membot':
      return contentCta.ctaText;
  }
}

function useCtaLink(contentCta: ContentCta): {
  text: string;
  href: string;
} {
  const defaultHref = useCtaHref();
  const { mediaId = 0 } = contentCta.type === 'immerse' ? contentCta : {};
  const mediaHref = useImmersiveVideoCtaHref(mediaId);
  const { asPath } = useRouter();
  const intl = useIntl();
  const isMobile = !useMediaQuerySSR(true, { minWidth: theme.sz.md });
  const membotQuery = useMemo(() => {
    if (contentCta.type !== 'membot') return { back: '', slug: '' };

    const baseUrl = asPath.split(/[?#]/)[0];
    const extendedUrl = contentCta.backAnchor ? `${baseUrl}#${contentCta.backAnchor}` : baseUrl;

    return { back: extendedUrl, slug: contentCta.membotSlug };
  }, [asPath, contentCta]);
  const membotHref = useExternalHref({
    linkType: 'memrise-membot',
    query: membotQuery,
  });
  const targetMsgs = useTargetMessages(localesWithMessages);

  switch (contentCta.type) {
    case 'interesting-content':
      return {
        text: isMobile
          ? intl.formatMessage(msgs.interestingContentMobileCta)
          : intl.formatMessage(targetMsgs.interestingContentCta),
        href: defaultHref,
      };
    case 'immerse':
      return {
        text: intl.formatMessage(msgs.immerseCta),
        href: mediaHref,
      };
    case 'membot':
      return {
        text: intl.formatMessage(msgs.membotCta),
        href: membotHref,
      };
  }
}

export const CTAContentBlock = ({ contentCta }: { contentCta: ContentCta }) => {
  const image = useCtaImage(contentCta);
  const text = useCtaText(contentCta);
  const link = useCtaLink(contentCta);

  const eventGeneratorClickCta = useEventGeneratorClickCta();

  return (
    <S.Container>
      <S.ImageWrapper>{image}</S.ImageWrapper>
      <S.Text>{text}</S.Text>
      <S.ButtonWrapper>
        <Button
          analyticsEvent={contentCta.type === 'membot' ? 'none' : eventGeneratorClickCta}
          buttonType="primary"
          buttonSize="large"
          fullWidth="never"
          href={link.href}
        >
          {link.text}
        </Button>
      </S.ButtonWrapper>
    </S.Container>
  );
};
